import React, { useState, useEffect } from "react"; 
import { motion, AnimatePresence } from "framer-motion"; 
import { X, Loader } from "lucide-react";
import httpClient from "../Api/axios";

import ScrollToTopButton from "./ScrollToTopButton";

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchGalleryImages = async () => {
      try {
        setLoading(true);
        const response = await httpClient.get("/gallery");
        
        // Assuming the API returns an array of objects with img and text properties
        setImages([...response.data].reverse());
        setLoading(false);
      } catch (err) {
        console.error("Error fetching gallery images:", err);
        setError("Failed to load gallery images. Please try again later.");
        setLoading(false);
      }
    };

    fetchGalleryImages();
  }, []);

  if (loading) {
    return (
      <section className="min-h-screen bg-white py-16 mt-[36px] px-6 md:px-10 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 mx-auto text-blue-500 animate-spin" />
          <p className="mt-4 text-gray-600 font-medium">Loading gallery images...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="min-h-screen bg-white py-16 mt-[36px] px-6 md:px-10 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <X className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Something went wrong</h3>
          <p className="text-gray-600">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-6 px-5 py-2 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition"
          >
            Retry
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-gradient-to-b from-white to-blue-50 py-16 mt-[36px] px-6 md:px-10">
      <div className="text-center mb-12">
        <div className="inline-block px-4 py-1 bg-blue-100 text-blue-700 rounded-full mb-3 font-medium">
          Gallery
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mt-2 mb-4">
          Our Clinic Moments
        </h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Take a look at some of the most beautiful glimpses of our clinic and patient care.
        </p>
      </div>

      {images.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <p className="text-gray-500">No gallery images available at the moment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-4 md:gap-6">
          {images.map((item, index) => (
            <motion.div
              key={index}
              className="overflow-hidden rounded-2xl shadow-xl cursor-pointer group relative"
              whileHover={{ scale: 1.03 }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              onClick={() => setSelectedImage(item.img)}
            >
              <img
                src={item.img}
                alt={item.text || `Gallery ${index + 1}`}
                className="w-full h-40 sm:h-56 md:h-52 object-cover rounded-2xl transition-all duration-300 ease-in-out group-hover:scale-105"
              />
              
              {/* {item.text && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 rounded-b-2xl transform transition-all duration-300">
                  <p className="text-white font-medium text-lg">{item.text}</p>
                </div>
              )} */}
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal Popup with Close Button */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 backdrop-blur-sm"
            onClick={() => setSelectedImage(null)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="relative max-w-4xl mx-auto"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", damping: 25 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute -top-6 -right-6 bg-white text-gray-800 rounded-full p-2 shadow-lg hover:bg-red-100 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
              <img
                src={selectedImage}
                alt="Selected"
                className="max-w-full max-h-[90vh] rounded-xl shadow-2xl"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <ScrollToTopButton />
    </section>
  );
};

export default Gallery;